// src/components/team-messages/ConversationListPanel.tsx
import { useState } from 'react';
import { Search, MessageCircle, SlidersHorizontal } from 'lucide-react';
import type { TeamConversation, OrgMember } from '@/types/teamMessages';

// ─── helpers ────────────────────────────────────────────────────────────────

function formatConvTime(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / 86_400_000);
  if (diffDays === 0) return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function getDmDisplayName(conv: TeamConversation, currentUserId: string, orgMembers: OrgMember[]): string {
  // Primary: name stamped at load time by getMyConversations() via the
  // get_dm_partner_names() RPC — keyed by conversation_id, no userId match needed.
  if (conv.name) return conv.name;

  // Secondary: orgMembers set-lookup (works when profiles.auth_user_id is correct).
  const memberIds = new Set(conv.members.map((m) => m.userId));
  const fromOrg = orgMembers.find(
    (m) => m.userId !== currentUserId && memberIds.has(m.userId),
  );
  if (fromOrg?.displayName) return fromOrg.displayName;

  // Tertiary: displayName resolved per-member in getMyConversations().
  const other = conv.members.find((m) => m.userId !== currentUserId);
  if (other?.displayName) return other.displayName;

  return 'Direct Message';
}

function getConvLabel(conv: TeamConversation, currentUserId: string, orgMembers: OrgMember[]): string {
  if (conv.type === 'dm') return getDmDisplayName(conv, currentUserId, orgMembers);
  if (conv.type === 'updates') return 'Updates';
  return conv.name ?? (conv.type === 'group' ? 'Group' : 'Channel');
}

function getTypeBadge(conv: TeamConversation): { label: string } {
  switch (conv.type) {
    case 'channel':  return { label: 'Channel' };
    case 'updates':  return { label: 'Updates' };
    case 'group':    return { label: 'Group' };
    case 'dm':
    default:         return { label: 'DM' };
  }
}

// ─── component ──────────────────────────────────────────────────────────────

interface ConversationListPanelProps {
  conversations: TeamConversation[];
  loadingConversations: boolean;
  currentUserId: string;
  orgMembers: OrgMember[];
  updatesChannelId: string | null;
  onSelectConversation: (conv: TeamConversation) => void;
}

export function ConversationListPanel({
  conversations,
  loadingConversations,
  currentUserId,
  orgMembers,
  updatesChannelId,
  onSelectConversation,
}: ConversationListPanelProps) {
  const [search, setSearch] = useState('');

  const q = search.toLowerCase().trim();

  // Filter by search
  const filtered = q
    ? conversations.filter((c) => {
        const label = getConvLabel(c, currentUserId, orgMembers);
        const lastBody = c.lastMessage?.body ?? '';
        return label.toLowerCase().includes(q) || lastBody.toLowerCase().includes(q);
      })
    : conversations;

  // Flat list: channels pinned first (general → updates → others), then sorted by updatedAt
  const channels = filtered.filter((c) => c.type === 'channel' || c.type === 'updates');
  const rest = filtered.filter((c) => c.type !== 'channel' && c.type !== 'updates');

  const sortedChannels = [
    ...channels.filter((c) => c.name?.toLowerCase() === 'general'),
    ...channels.filter((c) => c.id === updatesChannelId && c.name?.toLowerCase() !== 'general'),
    ...channels.filter((c) => c.id !== updatesChannelId && c.name?.toLowerCase() !== 'general'),
  ];

  const sortedRest = [...rest].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );

  const allConvs = [...sortedChannels, ...sortedRest];

  return (
    <div className="flex flex-col h-full bg-[#0a0a0a]">
      {/* Search */}
      <div className="px-3 py-2.5 border-b border-[#2e2e2e]">
        <div className="relative flex items-center">
          <Search className="absolute left-3 h-3.5 w-3.5 text-[#5a5a5a] pointer-events-none" />
          <input
            type="text"
            placeholder="Search conversations…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-9 py-2 text-sm bg-[#1e1e1e] rounded-lg border border-[#2e2e2e] outline-none focus:ring-1 focus:ring-white/20 placeholder:text-[#5a5a5a] text-[#fafafa]"
          />
          <SlidersHorizontal className="absolute right-3 h-3.5 w-3.5 text-[#5a5a5a]" />
        </div>
      </div>

      {/* Flat conversation list */}
      <div className="flex-1 overflow-y-auto">
        {loadingConversations ? (
          <div className="flex items-center justify-center h-24 text-sm text-[#8a8a8a]">
            Loading…
          </div>
        ) : allConvs.length === 0 ? (
          <div className="flex items-center justify-center h-24 text-sm text-[#8a8a8a]">
            {q ? 'No results' : 'No conversations yet'}
          </div>
        ) : (
          allConvs.map((conv, idx) => (
            <ConvRow
              key={conv.id}
              conv={conv}
              currentUserId={currentUserId}
              orgMembers={orgMembers}
              updatesChannelId={updatesChannelId}
              isLast={idx === allConvs.length - 1}
              onClick={() => onSelectConversation(conv)}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── unified row ─────────────────────────────────────────────────────────────

function ConvRow({
  conv,
  currentUserId,
  orgMembers,
  updatesChannelId,
  isLast,
  onClick,
}: {
  conv: TeamConversation;
  currentUserId: string;
  orgMembers: OrgMember[];
  updatesChannelId: string | null;
  isLast: boolean;
  onClick: () => void;
}) {
  const label = getConvLabel(conv, currentUserId, orgMembers);
  const badge = getTypeBadge(conv);
  const lastMsg = conv.lastMessage;
  const hasUnread = conv.unreadCount > 0;

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-start gap-3 px-4 py-3.5 hover:bg-[#1e1e1e] active:bg-[#2e2e2e] transition-colors text-left ${
        !isLast ? 'border-b border-[#2e2e2e]' : ''
      }`}
    >
      {/* Icon circle */}
      <span className="shrink-0 h-9 w-9 rounded-full bg-[#2e2e2e] flex items-center justify-center mt-0.5">
        <MessageCircle className="h-4 w-4 text-[#8a8a8a]" />
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Row 1: name + badge + timestamp */}
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className={`text-sm truncate flex-1 ${hasUnread ? 'font-semibold text-white' : 'font-medium text-[#fafafa]'}`}>
            {label}
          </span>
          {hasUnread ? (
            <span className="shrink-0 min-w-[18px] h-[18px] rounded-full bg-white text-black text-[10px] font-bold flex items-center justify-center px-1 tabular-nums">
              {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
            </span>
          ) : (
            <span className="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#2e2e2e] text-[#8a8a8a]">
              {badge.label}
            </span>
          )}
        </div>

        {/* Row 2: preview + timestamp */}
        <div className="flex items-center gap-1.5">
          <p className="text-xs text-[#8a8a8a] truncate flex-1 leading-4">
            {lastMsg
              ? (lastMsg.isSystem ? 'System update' : lastMsg.body)
              : 'No messages yet'}
          </p>
          {lastMsg && (
            <span className="shrink-0 text-[10px] text-[#5a5a5a] tabular-nums">
              {formatConvTime(lastMsg.createdAt)}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
